import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import './design-systems.css';
import App from './App';
import { AuthenticationResult, EventMessage, PublicClientApplication, EventType } from '@azure/msal-browser';
import { msalConfig } from './auth/authConfig';

/**
 * MSAL should be instantiated outside of the component tree
 * to prevent it from being re-instantiated on re-renders
 */
const msalInstance = new PublicClientApplication(msalConfig);

// Listen for sign-in event and set active account (e.g. popup flow)
msalInstance.addEventCallback((event: EventMessage) => {
  if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
    const payload = event.payload as AuthenticationResult;
    if (payload.account) {
      msalInstance.setActiveAccount(payload.account);
    }
  }
});

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

function renderApp() {
  root.render(
    <React.StrictMode>
      <App msalInstance={msalInstance} />
    </React.StrictMode>
  );
}

/**
 * Bootstrap: initialize MSAL, process redirect response (if returning from login),
 * then render. Must complete before first render so AuthenticatedTemplate sees the account.
 */
async function bootstrap() {
  await msalInstance.initialize();

  try {
    // Process redirect when user returns from Entra ID after loginRedirect()
    const redirectResponse = await msalInstance.handleRedirectPromise();
    if (redirectResponse?.account) {
      msalInstance.setActiveAccount(redirectResponse.account);
    }
  } catch (err) {
    // Do not block app startup on redirect-processing edge cases.
    console.warn('MSAL redirect handling error (continuing):', err);
  }

  if (!msalInstance.getActiveAccount() && msalInstance.getAllAccounts().length > 0) {
    msalInstance.setActiveAccount(msalInstance.getAllAccounts()[0]);
  }

  renderApp();
}

bootstrap().catch((err) => {
  console.error('MSAL bootstrap failed:', err);
  // Render app anyway so user can retry sign-in without a hard failure screen.
  renderApp();
});
